import { ReactElement, useMemo } from 'react';

import { CellContext, ColumnDef, HeaderContext } from '@tanstack/react-table';

import { Checkbox } from '@components/checkbox';
import { IconButton } from '@components/icon-button';
import { IconProps } from '@components/icons';
import { IconDropdown } from '@components/icons/dropdown';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';

import { SmallColumnIcon } from './column-icon';
import { customColumnClassName, withoutTitleClassName } from './table-utils';
import { AvailableColumn, DataItemType } from './types';
import {
  tableActionPrefix,
  selectionColumnWidth,
  fixedColumnWidth,
} from './use-column-sizing-vars';

export const useFixedStartColumns = <T extends DataItemType>(
  allowSelect: boolean,
  allowExpand: boolean,
) => {
  const fixedStartColumns = useMemo(() => {
    const columns: Array<ColumnDef<T, keyof T>> = [];

    if (allowSelect) {
      columns.push({
        accessorKey: `${tableActionPrefix}-select`,
        header: SelectionHeader,
        cell: SelectionCell,
        maxSize: selectionColumnWidth,
        minSize: selectionColumnWidth,
      } satisfies ColumnDef<T, keyof T>);
    }

    if (allowExpand) {
      columns.push({
        accessorKey: `${tableActionPrefix}-expand`,
        header: ExpandHeader,
        cell: ExpandCell,
        maxSize: fixedColumnWidth,
        minSize: fixedColumnWidth,
      } satisfies ColumnDef<T, keyof T>);
    }

    return columns;
  }, [allowExpand, allowSelect]);

  return fixedStartColumns;
};

export const useFixedEndColumns = <T extends DataItemType>(
  endFixedColumns: Array<AvailableColumn<T>>,
): Array<ColumnDef<T, keyof T>> => {
  return useMemo(() => {
    return endFixedColumns.map((column) => {
      const icon = (column.renderHeaderIcon?.() ??
        (column.type && <SmallColumnIcon type={column.type} />)) as
        | ReactElement<IconProps>
        | undefined;
      return {
        accessorKey: `${tableActionPrefix}-${column.id}`,
        header: () => {
          return (
            <Stack
              className={`${customColumnClassName}${
                !column.title ? ` ${withoutTitleClassName}` : ''
              }`}
              orientation="horizontal"
              align="center"
              space={1}
            >
              {icon}
              {column.title}
            </Stack>
          );
        },
        cell: ({ row }: CellContext<T, keyof T>) => {
          if (column.Cell) {
            return <column.Cell row={row.original} />;
          }
        },
        maxSize: fixedColumnWidth,
        minSize: fixedColumnWidth,
      } as ColumnDef<T, keyof T>;
    });
  }, [endFixedColumns]);
};

const SelectionHeader = <T extends DataItemType>({ table }: HeaderContext<T, keyof T>) => {
  const { t } = useScopedTranslate(locale);
  const allSelected = table.getIsAllRowsSelected();
  const someSelected = table.getIsSomeRowsSelected();

  return (
    <div className="table-selection">
      <Checkbox
        label={t('selectAllRows')}
        labelVisible={false}
        onChange={table.toggleAllRowsSelected}
        value={allSelected ? true : someSelected ? 'mixed' : false}
      />
    </div>
  );
};

const SelectionCell = <T extends DataItemType>({ row }: CellContext<T, keyof T>) => {
  const { t } = useScopedTranslate(locale);

  return (
    <div className="table-selection">
      <Checkbox
        label={t('selectRow')}
        labelVisible={false}
        value={row.getIsSelected()}
        onChange={row.toggleSelected}
      />
    </div>
  );
};

const ExpandHeader = <T extends DataItemType>({ table }: HeaderContext<T, keyof T>) => {
  return (
    <div className="table-expander">
      <IconButton
        size="md"
        variant="ghost-purple"
        onClick={() => table.toggleAllRowsExpanded()}
        icon={<IconDropdown rotateDegrees={table.getIsAllRowsExpanded() ? 0 : 270} />}
      />
    </div>
  );
};

const ExpandCell = <T extends DataItemType>({ row }: CellContext<T, keyof T>) => {
  return (
    <div className="table-expander">
      <IconButton
        size="md"
        variant="ghost-purple"
        onClick={() => row.toggleExpanded()}
        icon={<IconDropdown rotateDegrees={row.getIsExpanded() ? 0 : 270} />}
      />
    </div>
  );
};

const locale = {
  en: {
    selectRow: 'Select row',
    selectAllRows: 'Select all rows',
  },
};
